// ============================================================================
// TERMS & CONDITIONS — public page (/terms)
// Skeleton with placeholders. Have this reviewed by a lawyer before going live.
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import companyInfo, { formattedAddress } from './companyInfo';

const Terms = () => (
  <LegalLayout
    title="Terms and Conditions"
    subtitle={`The agreement between you and ${companyInfo.legalName} for the use of ${companyInfo.brandName}.`}
  >
    <section>
      <h2>1. Who we are</h2>
      <p>
        {companyInfo.brandName} is a subscription-based customer relationship management
        platform operated by {companyInfo.legalName}, a {companyInfo.entityType} registered
        in India at {formattedAddress}. In these terms, &ldquo;we&rdquo;, &ldquo;us&rdquo;
        and &ldquo;our&rdquo; refer to {companyInfo.legalName}, and &ldquo;you&rdquo; refers
        to the organisation that subscribes to the service and the individual users it
        authorises.
      </p>
      <p>
        By creating an account, purchasing a subscription, or using the platform in any way,
        you agree to these terms. If you do not agree, do not use the service.
      </p>
    </section>

    <section>
      <h2>2. The service</h2>
      <p>
        We provide access to a hosted, multi-tenant CRM application that includes lead
        management, follow-up scheduling, user and role administration, attendance tracking,
        reporting, and related features. Your organisation&rsquo;s data is stored in a
        dedicated database instance.
      </p>
      <p>
        We may add, change or withdraw individual features over time. Where a change
        materially reduces the functionality you have paid for, we will give reasonable
        prior notice.
      </p>
      <p className="legal__placeholder">
        [ADD ANY FEATURE-SPECIFIC TERMS, USAGE LIMITS, OR FAIR-USE CAPS HERE]
      </p>
    </section>

    <section>
      <h2>3. Accounts and seats</h2>
      <p>
        Access is licensed per user seat. Each seat is for one named individual and must not
        be shared between people. You are responsible for keeping login credentials
        confidential and for all activity that takes place under your organisation&rsquo;s
        account.
      </p>
      <p>
        You must tell us promptly if you become aware of any unauthorised access to your
        account.
      </p>
    </section>

    <section>
      <h2>4. Subscription, billing and renewal</h2>
      <p>
        Seats are billed on a single shared billing cycle. You select the cycle length at
        purchase, and all seats on the account expire on the same date. Current rates are
        published on our <Link to="/pricing">Pricing</Link> page.
      </p>
      <p>
        <strong>Renewal is manual.</strong> We do not store your payment instrument and we do
        not auto-debit. Your subscription will not renew unless an authorised administrator
        completes a payment before the cycle ends.
      </p>
      <p>
        Adding a user part-way through a cycle is charged on a prorated basis at the seat
        daily rate locked for that cycle, so the new seat lands on the same renewal date as
        the rest of the account.
      </p>
      <p>
        If a seat has lapsed and is brought back onto the shared cycle, a catch-up charge
        covers the days it was not paid for, so all seats align to one renewal date.
      </p>
      <p>
        All prices are in {companyInfo.pricing.currency} and are exclusive of applicable
        taxes unless stated otherwise. Taxes are charged at the rate in force on the date of
        invoice.
      </p>
    </section>

    <section>
      <h2>5. Payments</h2>
      <p>
        Payments are processed by our payment gateway partner. We do not receive or store
        your full card number, UPI PIN, CVV, or net banking credentials — these are handled
        entirely by the gateway under its own security standards.
      </p>
      <p>
        If a payment is taken but access is not granted due to a technical failure, the
        payment is reconciled automatically against the gateway and access is applied. If
        that does not happen, contact us and we will resolve it.
      </p>
    </section>

    <section>
      <h2>6. Suspension and expiry</h2>
      <p>
        When a subscription expires, access to the platform is suspended for the affected
        seats. Your data is retained during the retention period described in our{' '}
        <Link to="/privacy">Privacy Policy</Link>, and access is restored once payment is
        completed.
      </p>
      <p>
        We may suspend or terminate access immediately if you breach these terms, use the
        service unlawfully, or attempt to compromise the security or availability of the
        platform.
      </p>
    </section>

    <section>
      <h2>7. Your data</h2>
      <p>
        You retain ownership of the data you enter into the platform. You grant us a limited
        licence to host, process, back up and transmit that data solely to operate the
        service for you.
      </p>
      <p>
        You are responsible for ensuring that you have a lawful basis to collect and process
        the personal data of the leads, customers and employees you upload, and for complying
        with applicable data protection and telemarketing regulations, including
        do-not-disturb and consent requirements.
      </p>
    </section>

    <section>
      <h2>8. Acceptable use</h2>
      <p>You must not:</p>
      <ul>
        <li>use the service to send unsolicited or unlawful communications;</li>
        <li>upload malicious code, or attempt to gain access to another tenant&rsquo;s data;</li>
        <li>reverse engineer, resell, or sublicense the platform without written permission;</li>
        <li>use automated means to place unreasonable load on the service;</li>
        <li>use the service for any purpose that is illegal under Indian law.</li>
      </ul>
    </section>

    <section>
      <h2>9. Availability and support</h2>
      <p>
        We aim to keep the service available at all times but we do not guarantee
        uninterrupted access. Planned maintenance will be notified in advance where
        practicable.
      </p>
      <p className="legal__placeholder">
        [ADD YOUR SUPPORT SLA HERE — RESPONSE TIMES, SUPPORT CHANNELS, UPTIME COMMITMENT IF ANY]
      </p>
    </section>

    <section>
      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability arising out of or in
        connection with the service is limited to the amount you paid to us in the twelve
        months immediately preceding the event giving rise to the claim.
      </p>
      <p>
        We are not liable for indirect or consequential loss, loss of profit, loss of
        business, or loss of data to the extent such loss results from your own failure to
        maintain independent records.
      </p>
      <p className="legal__placeholder">
        [HAVE A LAWYER REVIEW THIS CLAUSE BEFORE PUBLISHING]
      </p>
    </section>

    <section>
      <h2>11. Changes to these terms</h2>
      <p>
        We may update these terms from time to time. The current version is always published
        on this page with its effective date. Continued use of the service after a change
        takes effect constitutes acceptance of the updated terms.
      </p>
    </section>

    <section>
      <h2>12. Governing law and jurisdiction</h2>
      <p>
        These terms are governed by the laws of India. The courts at{' '}
        {companyInfo.address.city}, {companyInfo.address.state} have exclusive jurisdiction
        over any dispute arising from them.
      </p>
    </section>

    <section>
      <h2>13. Contact</h2>
      <p>
        Questions about these terms can be sent to {companyInfo.supportEmail} or raised via
        our <Link to="/contact">Contact</Link> page.
      </p>
    </section>
  </LegalLayout>
);

export default Terms;
